import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  ListMusic,
  User,
  Music,
  Copy,
  Play,
  Loader2,
  Users,
  Clock,
  ChevronRight
} from "lucide-react";
import { toast } from "sonner";

export default function DiscoverPlaylistsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(null);

  useEffect(() => {
    fetchPlaylists();
  }, []);

  const fetchPlaylists = async () => {
    try {
      const response = await axios.get(`${API}/playlists/shared`);
      setPlaylists(response.data.playlists || []);
    } catch (error) {
      console.error("Failed to fetch shared playlists", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPlaylist = async (sourceUserId, ownerName) => {
    if (!user) {
      toast.info("Please sign in to copy playlists");
      navigate("/login");
      return;
    }

    setCopying(sourceUserId);
    try {
      await axios.post(`${API}/playlist/copy`, { source_user_id: sourceUserId });
      toast.success(`Copied ${ownerName}'s playlist to your library!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to copy playlist");
    } finally {
      setCopying(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="discover-playlists-page">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-gold/20 text-gold border-gold">
            <Users className="h-3 w-3 mr-1" /> Community
          </Badge>
          <h1 className="font-serif text-4xl font-bold mb-4">Discover Playlists</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Explore playlists shared by fellow Bar Songz members. Find new study music and copy your favorites!
          </p>
        </div>

        {/* Playlists Grid */}
        {playlists.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <ListMusic className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="font-serif text-xl font-semibold mb-2">No shared playlists yet</h2>
              <p className="text-muted-foreground mb-6">
                Be the first to share your playlist with the community!
              </p>
              <Button 
                onClick={() => navigate("/playlist")} 
                className="bg-gold text-primary-foreground hover:bg-gold/90"
              >
                Go to My Playlist
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {playlists.map((playlist) => (
              <Card 
                key={playlist.user_id} 
                className="hover:border-gold/50 transition-colors cursor-pointer group"
                onClick={() => navigate(`/playlist/shared/${playlist.user_id}`)}
                data-testid={`shared-playlist-${playlist.user_id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {playlist.owner_picture ? (
                        <img 
                          src={playlist.owner_picture} 
                          alt={playlist.owner_name}
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                          <User className="h-5 w-5 text-gold" />
                        </div>
                      )}
                      <div>
                        <CardTitle className="font-serif text-lg group-hover:text-gold transition-colors">
                          {playlist.owner_name}'s Playlist
                        </CardTitle>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Shared {formatDate(playlist.shared_at)}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-gold transition-colors" />
                  </div>
                </CardHeader>
                <CardContent>
                  {playlist.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      "{playlist.description}"
                    </p>
                  )}
                  
                  {/* Song count */}
                  <div className="flex items-center gap-2 mb-3">
                    <Music className="h-4 w-4 text-gold" />
                    <span className="text-sm font-medium">{playlist.total_songs} songs</span>
                  </div>

                  {/* Preview items */}
                  {playlist.preview_items.length > 0 && (
                    <div className="space-y-1 mb-4">
                      {playlist.preview_items.slice(0, 3).map((item, i) => (
                        <div key={i} className="text-xs text-muted-foreground truncate">
                          • {item.title} ({item.vibe})
                        </div>
                      ))}
                      {playlist.total_songs > 3 && (
                        <div className="text-xs text-gold">
                          +{playlist.total_songs - 3} more...
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <Button
                    size="sm"
                    className="w-full bg-gold text-primary-foreground hover:bg-gold/90"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyPlaylist(playlist.user_id, playlist.owner_name);
                    }}
                    disabled={copying === playlist.user_id}
                  >
                    {copying === playlist.user_id ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <Copy className="h-4 w-4 mr-1" />
                    )}
                    Copy Playlist
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Back to community link */}
        <div className="mt-8 text-center">
          <Button variant="outline" onClick={() => navigate("/community")}>
            <Users className="h-4 w-4 mr-2" />
            Back to Community
          </Button>
        </div>
      </div>
    </div>
  );
}
